import { Box, HStack, VStack } from '@web/components/atoms/Layout'
import { usePageStore } from 'apps/fe-donation/libs/hooks/usePageStore'
import { Image } from '@web/components/atoms/Image'
import { PageButton } from '@web/components/page-components/PageButton'
import { useMemo, useState } from 'react'
import { ShareIcon } from '@web/components/atoms/Icons'
import { VIRTUAL_GIFT_METADATA_KEY } from './utils'
import { VirtualGiftSection } from '@web/graphql-components'
import { DESKTOP_CONTAINER_QUERY, MOBILE_CONTAINER_QUERY } from '@web/components/page-components/utils'
import SocialShare from '../SocialShare'
import { DEFAULT_SOCIAL_SHARE } from '@web/components/page-components/default'
import { usePaymentStore } from 'apps/fe-donation/libs/hooks/usePaymentStore'
import { useSearchParams } from 'next/navigation'
import environment from 'apps/fe-donation/libs/environment'
import useShareableUrl from 'apps/fe-donation/libs/hooks/useShareableUrl'

type VirtualGiftItemProps = {
  url: string
  imageUrl: string
  index: number
  isIOS: boolean
  giftId?: string
  paymentTransactionId?: string
  styles?: VirtualGiftSection['styles']
  name?: string
}

const VirtualGiftItem = ({
  url,
  imageUrl,
  index,
  isIOS,
  giftId,
  paymentTransactionId,
  styles,
}: VirtualGiftItemProps) => {
  const giftParams = paymentTransactionId
    ? {
        gift: `${paymentTransactionId}_${index}`,
      }
    : { gift: giftId ? `${giftId}_${index}` : '' }

  const [isDownloading, setIsDownloading] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const shareableUrl = useShareableUrl({ searchParams: giftParams })

  return (
    <VStack>
      <Image
        maxW={styles?.maxItemWidth === '0px' || !styles?.maxItemWidth ? '100%' : styles?.maxItemWidth}
        w="100%"
        src={imageUrl}
        alt={`Virtual Gift ${index + 1}`}
        objectFit="contain"
      />
      <Box
        css={{
          [DESKTOP_CONTAINER_QUERY]: {
            display: 'block',
          },
          [MOBILE_CONTAINER_QUERY]: {
            display: 'none',
          },
        }}
        w="100%"
      >
        <SocialShare
          {...DEFAULT_SOCIAL_SHARE}
          styles={{ ...DEFAULT_SOCIAL_SHARE.styles, layooutDisplay: 'dropdown' }}
          searchParams={giftParams}
        />
      </Box>
      <Box
        css={{
          [MOBILE_CONTAINER_QUERY]: {
            display: 'block',
          },
          [DESKTOP_CONTAINER_QUERY]: {
            display: 'none',
          },
        }}
        w="100%"
      >
        {navigator?.share ? (
          <PageButton
            name="Primary"
            buttonText="Share"
            iconSlot={<ShareIcon />}
            onClick={async () => {
              try {
                setIsSharing(true)
                const res = await fetch(`/api/v1/image-proxy?url=${encodeURIComponent(url)}`)

                const file = await res
                  .blob()
                  .then((blob) => new File([blob], `virtual-gift-${index + 1}.png`, { type: blob.type }))

                const files = [file]

                const sharedUrl = new URL(shareableUrl)
                sharedUrl.searchParams.set('gift', giftParams.gift)

                const canShareFiles = !isIOS && navigator.canShare && navigator.canShare({ files })

                navigator
                  .share({
                    title: `${name ?? 'Virtual gift'} ${index + 1}`,
                    text: styles?.options?.shareText || 'Check out my virtual gift!',
                    url: sharedUrl.toString(),
                    ...(canShareFiles ? { files } : {}),
                  })
                  .catch((error) => console.error('Error sharing:', error))
              } finally {
                setIsSharing(false)
              }
            }}
            isLoading={isSharing}
            width="100%"
          />
        ) : (
          <SocialShare
            {...DEFAULT_SOCIAL_SHARE}
            styles={{ ...DEFAULT_SOCIAL_SHARE.styles, layooutDisplay: 'dropdown' }}
            searchParams={giftParams}
          />
        )}
      </Box>
      <Box w="100%">
        <PageButton
          name="Primary"
          buttonText="Download"
          onClick={async () => {
            try {
              setIsDownloading(true)
              const res = await fetch(`/api/v1/image-proxy?url=${encodeURIComponent(url)}`)
              const blob = await res.blob()
              try {
                if (
                  isIOS &&
                  navigator?.canShare &&
                  navigator.canShare({ files: [new File([blob], 'image.png', { type: blob.type })] })
                ) {
                  const file = new File([blob], `virtual-gift-${index + 1}.png`, { type: blob.type })
                  await navigator.share({
                    files: [file],
                  })

                  return
                }
              } catch {
                // swallow and fallback to just download
              }

              const link = document.createElement('a')
              const objectUrl = URL.createObjectURL(blob)
              link.href = objectUrl
              link.download = styles.options?.fileName ?? `virtual-seat-${index + 1}.png`
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
              URL.revokeObjectURL(objectUrl)
            } catch (error) {
              console.error('Error downloading file:', error)
            } finally {
              setIsDownloading(false)
            }
          }}
          isLoading={isDownloading}
          width="100%"
        />
      </Box>
    </VStack>
  )
}

const VirtualGiftSectionItemDownload = (props: VirtualGiftSection) => {
  const paymentTransactionId = usePaymentStore((state) => state.paymentTransactionId)
  const searchParams = useSearchParams()
  const charityId = usePageStore((state) => state.charityId)
  const campaignId = usePageStore((state) => state.campaignId)
  const giftId = searchParams.get('giftId') ?? ''
  const virtualGift = useMemo(() => {
    const giftFromStore = usePageStore.getState().virtualGift
    if (giftFromStore) {
      return giftFromStore
    }

    const giftIdFromSearchParams = searchParams.get('giftId')
    const giftNumberFromSearchParams = searchParams.get('giftNumber')

    try {
      if (giftIdFromSearchParams && giftNumberFromSearchParams && charityId && campaignId) {
        const giftNumber = parseInt(giftNumberFromSearchParams, 10)
        const gift = {
          items: Array.from({ length: giftNumber }, (_, index) => {
            const imageUrl = `${environment.adminAssetsUrl}charities/${charityId}/campaigns/${campaignId}/virtual-gifts/virtual-gift-${giftIdFromSearchParams}_${index}.png`
            return {
              url: imageUrl,
              imageUrl,
            }
          }),
        }
        return gift
      }
    } catch {
      // just swallow
    }

    const giftFromStorage =
      typeof window !== 'undefined' ? window.localStorage.getItem(VIRTUAL_GIFT_METADATA_KEY) : null
    if (giftFromStorage) {
      try {
        const gift = JSON.parse(giftFromStorage)
        return gift
      } catch {
        return
      }
    }

    return
  }, [searchParams, charityId, campaignId])

  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false

    // Check for iOS/iPadOS
    const userAgent = navigator.userAgent.toLowerCase()
    const isIOSDevice = /iphone|ipod/.test(userAgent)
    // Modern iPad detection: iPadOS 13+ reports as Mac, but has touch support
    const isIPad = /ipad/.test(userAgent) || (navigator.maxTouchPoints > 1 && /mac/.test(userAgent))

    return isIOSDevice || isIPad
  }, [])

  return (
    <HStack
      w="100%"
      flexWrap="wrap"
      gap={4}
      justifyContent="center"
      css={{
        [DESKTOP_CONTAINER_QUERY]: {
          flexDirection: 'row',
        },
        [MOBILE_CONTAINER_QUERY]: {
          flexDirection: 'column',
        },
      }}
    >
      {virtualGift?.items?.map((gift: any, index: number) => (
        <VirtualGiftItem
          imageUrl={gift.imageUrl}
          url={gift.url}
          index={index}
          isIOS={isIOS}
          giftId={giftId}
          paymentTransactionId={paymentTransactionId ?? ''}
          key={gift.url}
          name={virtualGift?.options?.name}
          styles={props?.styles}
        />
      ))}
    </HStack>
  )
}

export default VirtualGiftSectionItemDownload
